import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles, CheckCircle, ShieldCheck } from "lucide-react";
import BlueprintBackdrop from "../components/BlueprintBackdrop";
import BrandWordmark from "../components/BrandWordmark";
import MemoRenderer from "../components/MemoRenderer";
import SEOHead from "../components/SEOHead";
import CountyNavLinks from "../components/CountyNavLinks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SampleMemoPage = () => {
  const navigate = useNavigate();
  const [memo, setMemo] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/sample-memo`);
        if (!r.ok) throw new Error("Failed to load sample");
        const data = await r.json();
        if (!cancelled) setMemo(data.memo || "");
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load sample memo");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="sample-memo-page">
      <SEOHead
        title="Sample AI Compliance Memo for California ADU Permits"
        description="See a live sample of the AI-generated CALGreen compliance cover memo we ship with every paid CWMP. Plain-English, code-cited, council-ready in 60 seconds."
        keywords="ADU compliance memo sample, CALGreen cover letter, California ADU permit cover memo, plan checker memo, AI permit memo"
        path="/sample-memo"
        image="https://www.plangeneratorca.com/og-image.svg"
      />
      {/* Top nav */}
      <nav className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-5">
              <BrandWordmark size="md" onClick={() => navigate("/")} />
              <CountyNavLinks className="hidden lg:flex" />
            </div>
            <button
              onClick={() => navigate("/")}
              className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              data-testid="back-home-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Home
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-16 pb-12 px-4 overflow-hidden">
        <BlueprintBackdrop variant="hero" showIsometric={false} />
        <div className="relative max-w-4xl mx-auto text-center z-10">
          <div className="pre-headline-pill mb-5 inline-flex">
            <Sparkles className="w-3 h-3" />
            AI Compliance Memo · Live Preview
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight leading-[0.95]">
            See the cover memo your <span className="headline-accent">plan checker actually wants.</span>
          </h1>
          <p className="mt-5 text-base text-muted-foreground max-w-2xl mx-auto">
            Every paid CWMP includes an AI-generated, plain-English compliance memo with code citations, ready to staple onto your permit package. Below is a live sample for a 720&nbsp;sq&nbsp;ft Los Angeles ADU.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate("/register")}
              data-testid="sample-memo-cta"
              className="pill-btn pill-btn-primary text-base px-7 py-3.5"
            >
              Get started
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/#pricing")}
              className="pill-btn pill-btn-secondary text-base px-7 py-3.5"
            >
              See pricing
            </button>
          </div>
        </div>
      </section>

      {/* Memo card */}
      <section className="px-4 pb-20">
        <div className="max-w-3xl mx-auto">
          <div className="surface-card overflow-hidden">
            {/* Letterhead */}
            <div className="bg-foreground text-white p-6 relative overflow-hidden">
              <div className="blueprint-grid-dense" aria-hidden="true" style={{ opacity: 0.3 }} />
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <div className="text-[0.65rem] uppercase tracking-[0.18em] text-white/60 font-bold">
                    Sample · For demonstration only
                  </div>
                  <div className="font-display font-black text-2xl mt-1">
                    Compliance Cover Memo
                  </div>
                </div>
                <ShieldCheck className="w-8 h-8 text-accent" />
              </div>
            </div>

            {/* Memo body */}
            <div className="p-6 sm:p-10">
              {loading && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-10 h-10 rounded-full border-2 border-accent border-t-transparent animate-spin mb-4" />
                  <p className="text-sm text-muted-foreground">Generating live sample…</p>
                </div>
              )}
              {error && (
                <div className="text-sm text-[#8B1F1F] bg-[#FCD9D6] rounded-xl p-4 text-center">
                  {error}. Please try again later.
                </div>
              )}
              {!loading && memo && (
                <MemoRenderer markdown={memo} data-testid="sample-memo-content" />
              )}
            </div>

            {/* Footer disclaimer */}
            <div className="border-t border-border bg-muted/40 p-5 text-xs text-muted-foreground">
              This is a sample. Actual memos are generated from your specific project data
              entered in the wizard, including county, materials, and diversion calculations.
              Memos are advisory only — final compliance is determined by your jurisdiction.
            </div>
          </div>

          {/* Value props */}
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            <div className="surface-card p-5">
              <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-3">
                <Sparkles className="w-5 h-5" />
              </div>
              <h3 className="font-display font-bold text-base text-foreground mb-1">Plain English</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">No code-jockey jargon. Reviewers can stamp it in 60 seconds.</p>
            </div>
            <div className="surface-card p-5">
              <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-3">
                <CheckCircle className="w-5 h-5" />
              </div>
              <h3 className="font-display font-bold text-base text-foreground mb-1">Code-cited</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">CALGreen §5.408, Title 24 Part 11, county portals — every claim sourced.</p>
            </div>
            <div className="surface-card p-5">
              <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-3">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h3 className="font-display font-bold text-base text-foreground mb-1">Approval-friendly</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">Designed to reduce rework and pre-empt the "please clarify" follow-up.</p>
            </div>
          </div>

          {/* Final CTA */}
          <div className="mt-12 text-center">
            <h2 className="font-display font-black text-3xl text-foreground">
              Your AI memo is <span className="headline-accent">included</span>.
            </h2>
            <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto">
              Generated automatically with every paid CWMP. Bundle and Subscription plans get unlimited regenerations.
            </p>
            <button
              onClick={() => navigate("/register")}
              className="pill-btn pill-btn-primary mt-6 text-base px-7 py-3.5"
              data-testid="sample-memo-final-cta"
            >
              Create your account
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* County cross-links for SEO + discovery */}
          <div className="mt-14 pt-10 border-t border-border">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-4">Local guides</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <button
                onClick={() => navigate("/california/los-angeles-adu-permit")}
                className="surface-card p-4 text-left hover:shadow-md transition-shadow"
                data-testid="sample-related-la"
              >
                <div className="font-display font-bold text-sm text-foreground">Los Angeles</div>
                <p className="text-xs text-muted-foreground mt-1">LADBS + LA County toolkit</p>
              </button>
              <button
                onClick={() => navigate("/california/san-diego-adu-permit")}
                className="surface-card p-4 text-left hover:shadow-md transition-shadow"
                data-testid="sample-related-sd"
              >
                <div className="font-display font-bold text-sm text-foreground">San Diego</div>
                <p className="text-xs text-muted-foreground mt-1">DSD + SDMC §142 toolkit</p>
              </button>
              <button
                onClick={() => navigate("/california/bay-area-adu-permit")}
                className="surface-card p-4 text-left hover:shadow-md transition-shadow"
                data-testid="sample-related-bay"
              >
                <div className="font-display font-bold text-sm text-foreground">Bay Area</div>
                <p className="text-xs text-muted-foreground mt-1">9-county ADU toolkit</p>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SampleMemoPage;
